import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CurrentInstall {
  case_id: number
  case_name: string
  installed_by: string | null
  installed_at: string
  vehicle: string | null
  gps_location: string | null
  vehicle_location: string | null
}

interface GpsDevice {
  id: number
  name: string
  serial_number: string | null
  current_user: string | null
  status: number
  sort_order: number
  current_install?: CurrentInstall
}

interface Props {
  gps_devices: GpsDevice[]
  status_filter: string
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "GPS Devices", href: "/gps_devices" },
]

function formatDate(dateStr: string): string {
  return formatDateUtil(dateStr, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
}

export default function Index({ gps_devices, status_filter }: Props) {
  const isActive = status_filter === "active"
  const title = isActive ? "GPS Units" : "Inactive GPS Units"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={title} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{title}</h1>
          <Link
            href="/gps_devices/new"
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add GPS Device
          </Link>
        </div>

        {gps_devices.length > 0 ? (
          <ul className="space-y-1">
            {gps_devices.map((device) => (
              <li
                key={device.id}
                className={`hover:bg-hpi-pale-blue rounded border border-transparent px-4 py-2 ${
                  !isActive ? "opacity-50" : ""
                }`}
              >
                <div className="flex items-baseline justify-between">
                  <Link
                    href={`/gps_devices/${device.id}`}
                    className="text-hpi-active-blue font-medium hover:underline"
                  >
                    {device.name}
                  </Link>
                  <Link
                    href={`/gps_devices/${device.id}/edit`}
                    className="text-hpi-active-blue text-sm hover:underline"
                  >
                    Edit
                  </Link>
                </div>

                {device.current_user && (
                  <div className="text-hpi-dark-gray mt-1 ml-4 text-sm">
                    Held by: {device.current_user}
                  </div>
                )}
                {device.current_install && (
                  <div className="text-hpi-dark-gray mt-1 ml-4 text-sm">
                    <div>
                      On case:{" "}
                      <Link
                        href={`/cases/${device.current_install.case_id}`}
                        className="text-hpi-active-blue hover:underline"
                      >
                        {device.current_install.case_name}
                      </Link>
                      {" - Installed"}
                      {device.current_install.installed_by &&
                        ` by ${device.current_install.installed_by}`}
                      {device.current_install.installed_at &&
                        ` on ${formatDate(device.current_install.installed_at)}`}
                    </div>
                    {device.current_install.vehicle && (
                      <div className="ml-4">
                        Vehicle: {device.current_install.vehicle}
                      </div>
                    )}
                    {device.current_install.gps_location && (
                      <div className="ml-4">
                        Location on Vehicle:{" "}
                        {device.current_install.gps_location}
                      </div>
                    )}
                    {device.current_install.vehicle_location && (
                      <div className="ml-4">
                        Location of Vehicle at time of installation:{" "}
                        {device.current_install.vehicle_location}
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-hpi-med-gray">No GPS devices found.</p>
        )}

        <div className="mt-6">
          <button
            type="button"
            onClick={() =>
              router.get(
                "/gps_devices",
                { status: isActive ? "inactive" : "active" },
                { preserveState: false },
              )
            }
            className="text-hpi-active-blue text-sm hover:underline"
          >
            {isActive ? "View Inactive" : "View Active"}
          </button>
        </div>
      </div>
    </AppLayout>
  )
}
